import React, { useState } from 'react';
import { Card, Row, Col, Table, Tag, Alert, Button, Modal } from 'antd';
import { motion } from 'framer-motion';
import { 
  GlobalOutlined,
  SendOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  EyeOutlined
} from '@ant-design/icons';

interface FlightData {
  key: string;
  flightCode: string;
  airline: string;
  origin: string;
  destination: string;
  route: string;
}

const flightData: FlightData[] = [
  { key: '1', flightCode: '5Y', airline: '美国亚特拉斯航空', origin: 'EHU/NGK/CSX', destination: 'JFK', route: 'EHU/NGK/CSX-JFK' },
  { key: '2', flightCode: 'CZ', airline: '中国南方航空', origin: 'CAN', destination: 'JFK', route: 'CAN-JFK' },
  { key: '3', flightCode: 'OZ', airline: '韩亚航空', origin: 'CAN', destination: 'JFK', route: 'CAN-ICN-JFK' },
  { key: '4', flightCode: 'JL', airline: '日本航空', origin: 'PEK', destination: 'JFK', route: 'PEK-HND-JFK' },
  { key: '5', flightCode: 'M6', airline: '马士基', origin: 'HGH', destination: 'ORD', route: 'HGK-RFD-ORD' },
  { key: '6', flightCode: 'PO', airline: '美国博立航空', origin: 'CAN', destination: 'ORD', route: 'CAN-CVG-ORD' },
  { key: '7', flightCode: '5Y', airline: '美国亚特拉斯航空', origin: 'HKG/EHU', destination: 'ORD', route: 'HKG-EHU-ORD' },
  { key: '8', flightCode: 'Y8', airline: '金鹏航空', origin: 'NGK', destination: 'ORD', route: 'NGK-ORD' },
  { key: '9', flightCode: 'JL', airline: '日本航空', origin: 'PEK/TSN', destination: 'ORD', route: 'PEK/TSN-HND-ORD' },
  { key: '10', flightCode: 'CA', airline: '中国国际航空', origin: 'CAN/SZX', destination: 'LAX', route: 'CAN/SZX-PEK-LAX' },
  { key: '11', flightCode: 'JL', airline: '日本航空', origin: 'CAN/TSN', destination: 'LAX', route: 'CAN/TSN-HND-LAX' },
  { key: '12', flightCode: 'UA', airline: '美国联合航空', origin: 'CAN/HKG', destination: 'LAX', route: 'CAN-NRT-LAX\nHKG-SFO-LAX' },
  { key: '13', flightCode: 'CZ', airline: '中国南方航空', origin: 'CAN', destination: 'LAX', route: 'CAN-LAX' },
  { key: '14', flightCode: 'KE', airline: '大韩航空', origin: 'CAN', destination: 'LAX', route: 'CAN-ICN-LAX' },
  { key: '15', flightCode: 'JL', airline: '日本航空', origin: 'CAN/TSN', destination: 'SFO', route: 'CAN/TSN-HND-SFO' },
  { key: '16', flightCode: 'UA', airline: '美国联合航空', origin: 'HKG/PEK', destination: 'SFO', route: 'HKG/PEK-SFO' },
  { key: '17', flightCode: 'KE', airline: '大韩航空', origin: 'CAN', destination: 'SFO', route: 'CAN-ICN-SFO' },
  { key: '18', flightCode: 'OZ', airline: '韩亚航空', origin: 'CAN', destination: 'SFO', route: 'CAN-ICN-SFO' },
  { key: '19', flightCode: 'BR', airline: '台湾长荣航空', origin: 'HKG/XMN', destination: 'SFO', route: 'HKG/XMN-TPE-SFO' }
];

const flightColumns = [
  { title: '航司代码', dataIndex: 'flightCode', key: 'flightCode', width: 80 },
  { title: '航司名称', dataIndex: 'airline', key: 'airline', width: 150 },
  { title: '起运港', dataIndex: 'origin', key: 'origin', width: 120 },
  { title: '目的港', dataIndex: 'destination', key: 'destination', width: 80 },
  { 
    title: '航班路线', 
    dataIndex: 'route', 
    key: 'route',
    render: (text: string) => (
      <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>{text}</span>
    )
  }
];

const airportInfo = [
  {
    region: '美西清关（邮编8、9开头）',
    airports: ['LAX', 'SFO'],
    description: '美西主要机场，清关效率高'
  },
  {
    region: '美中清关（邮编4-7开头）',
    airports: ['ORD'],
    description: '美中主要机场'
  },
  {
    region: '美东清关（邮编0-3开头）',
    airports: ['JFK'],
    description: '美东主要机场'
  }
];

const FlightInfoAndMap: React.FC = () => {
  const [mapModalVisible, setMapModalVisible] = useState(false);
  const [selectedMap, setSelectedMap] = useState<'lax' | 'jfk' | null>(null);

  const showMapModal = (mapType: 'lax' | 'jfk') => {
    setSelectedMap(mapType);
    setMapModalVisible(true);
  };

  return (
    <section id="flight-info" style={{ 
      padding: '80px 0', 
      background: '#f5f5f7',
      position: 'relative'
    }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: 'center', marginBottom: '60px' }}
        >
          <h2 className="display-medium" style={{ 
            marginBottom: '24px',
            color: '#1d1d1f'
          }}>
            航班信息与美国清关机场
          </h2>
          <p className="body-large" style={{ 
            color: '#6e6e73', 
            maxWidth: '800px', 
            margin: '0 auto',
            fontSize: '1.125rem'
          }}>
            详细的航班信息和美国主要清关机场分布，助您了解运输网络
          </p>
        </motion.div>

        <Row gutter={[32, 32]}>
          {/* 航班信息 */}
          <Col xs={24} lg={14}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Card
                style={{
                  background: '#ffffff',
                  border: '1px solid #d2d2d7',
                  borderRadius: '16px',
                  height: '100%',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                }}
                bodyStyle={{ padding: '24px' }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginBottom: '20px' 
                }}>
                  <SendOutlined style={{ color: '#0071e3', fontSize: '20px' }} />
                  <h3 style={{ 
                    color: '#1D1D1F',
                    margin: 0,
                    fontSize: '1.25rem',
                    fontWeight: '600'
                  }}>
                    国内常用航班
                  </h3>
                </div>

                <Alert
                  message="航班优势"
                  description="覆盖中国主要城市到美国四大机场的航线，为您提供灵活的运输选择"
                  type="info"
                  showIcon
                  style={{ marginBottom: '20px' }}
                />

                <div style={{ 
                  maxHeight: '400px', 
                  overflowY: 'auto',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px'
                }}>
                  <Table
                    columns={flightColumns}
                    dataSource={flightData}
                    pagination={false}
                    size="small"
                    scroll={{ x: 600 }}
                  />
                </div>
              </Card>
            </motion.div>
          </Col>

          {/* 机场信息 */}
          <Col xs={24} lg={10}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Card
                style={{
                  background: '#ffffff',
                  border: '1px solid #d2d2d7',
                  borderRadius: '16px',
                  height: '100%',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                }}
                bodyStyle={{ padding: '24px' }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginBottom: '20px' 
                }}>
                  <EnvironmentOutlined style={{ color: '#0071e3', fontSize: '20px' }} />
                  <h3 style={{ 
                    color: '#1D1D1F',
                    margin: 0,
                    fontSize: '1.25rem',
                    fontWeight: '600'
                  }}>
                    美国清关主要机场
                  </h3>
                </div>

                {airportInfo.map((info, index) => (
                  <div key={index} style={{ 
                    marginBottom: '20px',
                    padding: '16px',
                    background: '#f0f9ff',
                    borderRadius: '8px',
                    border: '1px solid #91d5ff'
                  }}>
                    <h4 style={{ 
                      color: '#1890ff', 
                      marginBottom: '8px',
                      fontSize: '1rem',
                      fontWeight: '600'
                    }}>
                      {info.region}
                    </h4>
                    <div style={{ marginBottom: '8px' }}>
                      {info.airports.map((airport) => (
                        <Tag key={airport} color="blue" style={{ marginRight: '8px' }}>
                          {airport}
                        </Tag>
                      ))}
                    </div>
                    <p style={{ 
                      color: '#666', 
                      margin: 0, 
                      fontSize: '16px' 
                    }}>
                      {info.description}
                    </p>
                  </div>
                ))}

                <div style={{ marginTop: '24px' }}>
                  <h4 style={{ 
                    color: '#1D1D1F', 
                    marginBottom: '16px',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}>
                    美国快递派送时效地图
                  </h4>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <Button
                        type="primary"
                        size="small"
                        block
                        icon={<EyeOutlined />}
                        onClick={() => showMapModal('lax')}
                        style={{ 
                          background: '#52c41a',
                          borderColor: '#52c41a',
                          borderRadius: '6px'
                        }}
                      >
                        LAX提取时效
                      </Button>
                    </Col>
                    <Col span={12}>
                      <Button
                        type="primary"
                        size="small"
                        block
                        icon={<EyeOutlined />}
                        onClick={() => showMapModal('jfk')}
                        style={{ 
                          background: '#1890ff',
                          borderColor: '#1890ff',
                          borderRadius: '6px'
                        }}
                      >
                        JFK提取时效
                      </Button>
                    </Col>
                  </Row>
                </div>
              </Card>
            </motion.div>
          </Col>
        </Row>

        {/* 服务特点 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          style={{ marginTop: '60px' }}
        >
          <Card
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: 'none',
              borderRadius: '16px'
            }}
            bodyStyle={{ padding: '40px' }}
          >
            <Row gutter={[32, 32]} align="middle">
              <Col xs={24} md={8}>
                <div style={{ textAlign: 'center' }}>
                  <SendOutlined style={{ fontSize: '64px', color: '#1890ff', marginBottom: '16px' }} />
                  <h3 style={{ color: '#1D1D1F', marginBottom: '12px' }}>多航线覆盖</h3>
                  <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
                    覆盖国内主要城市到美国四大机场的直飞和中转航线
                  </p>
                </div>
              </Col>
              
              <Col xs={24} md={8}>
                <div style={{ textAlign: 'center' }}>
                  <ClockCircleOutlined style={{ fontSize: '64px', color: '#52c41a', marginBottom: '16px' }} />
                  <h3 style={{ color: '#1D1D1F', marginBottom: '12px' }}>稳定班期</h3>
                  <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
                    每周固定航班，保证货物运输的稳定性和可预期性
                  </p>
                </div>
              </Col>
              
              <Col xs={24} md={8}>
                <div style={{ textAlign: 'center' }}>
                  <GlobalOutlined style={{ fontSize: '64px', color: '#faad14', marginBottom: '16px' }} />
                  <h3 style={{ color: '#1D1D1F', marginBottom: '12px' }}>全美覆盖</h3>
                  <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
                    从主要机场到全美50州的完整配送网络
                  </p>
                </div>
              </Col>
            </Row>
          </Card>
        </motion.div>

        {/* 时效地图模态框 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <EnvironmentOutlined />
              <span>美国快递派送时效地图</span>
            </div>
          }
          open={mapModalVisible}
          onCancel={() => setMapModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setMapModalVisible(false)}>
              关闭
            </Button>
          ]}
          width={800}
        >
          <div style={{ textAlign: 'center' }}>
            {selectedMap === 'lax' && (
              <div>
                <h4 style={{ marginBottom: '16px' }}>LAX机场提取时效分布</h4>
                <Alert
                  message="时效说明"
                  description="从LAX机场提取后，通过UPS/FedEx派送到全美各地的时效分布图"
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
                <div style={{ 
                  width: '100%', 
                  height: '400px',
                  background: '#f0f2f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center', color: '#666' }}>
                    <EnvironmentOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                    <p>LAX提取时效地图</p>
                    <p style={{ fontSize: '14px' }}>加州及周边州1-2天，其他州2-5天</p>
                  </div>
                </div>
              </div>
            )}
            
            {selectedMap === 'jfk' && (
              <div>
                <h4 style={{ marginBottom: '16px' }}>JFK机场提取时效分布</h4>
                <Alert
                  message="时效说明"
                  description="从JFK机场提取后，通过UPS/FedEx派送到全美各地的时效分布图"
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
                <div style={{ 
                  width: '100%', 
                  height: '400px',
                  background: '#f0f2f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center', color: '#666' }}>
                    <EnvironmentOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                    <p>JFK提取时效地图</p>
                    <p style={{ fontSize: '14px' }}>纽约及周边州1-2天，其他州2-6天</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal>
      </div>

      {/* 背景装饰 */}
      <div style={{
        position: 'absolute',
        top: '20%',
        left: '-10%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        zIndex: 1
      }} />
      
      <div style={{
        position: 'absolute',
        bottom: '20%',
        right: '0%',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        zIndex: 1
      }} />
    </section>
  );
};

export default FlightInfoAndMap;

